
import React, { useState, useEffect, useRef } from 'react';
import * as api from '../utils/api';
import styles from './DepositModal.module.css';
import { FaTimes, FaCopy, FaArrowLeft, FaBackspace, FaUniversity, FaQrcode, FaCheckCircle, FaCloudUploadAlt, FaSpinner } from 'react-icons/fa';
import { FiSmartphone, FiMail, FiEdit2, FiSave, FiSend, FiCheck } from 'react-icons/fi';
import MessageBox from './MessageBox';
import { useAuth } from '../context/AuthContext';
import { useProgressBar } from '../context/ProgressBarContext';

function DepositModal({ onClose, initialAmount, initialTransaction }) {
  const [step, setStep] = useState(initialTransaction ? 5 : 1);
  const [amount, setAmount] = useState(initialAmount || '');
  const [transaction, setTransaction] = useState(initialTransaction || null);

  const [paymentMethod, setPaymentMethod] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [adminBankDetails, setAdminBankDetails] = useState(null);
  const { user: authUser, refreshUser } = useAuth();
  const [verificationType, setVerificationType] = useState('email'); // 'email' or 'mobile'
  const [pushedPaymentDetails, setPushedPaymentDetails] = useState(null);

  // Matching AccountStatusPage verification card state
  const [targetValue, setTargetValue] = useState('');
  const [isOtpApproved, setIsOtpApproved] = useState(false);
  const [isEditingField, setIsEditingField] = useState(false);
  const [isOtpSubmitted, setIsOtpSubmitted] = useState(false);
  const [fieldSavedNotice, setFieldSavedNotice] = useState(false);
  const fieldInputRef = useRef(null);
  const [isInfoLineHighlighted, setInfoLineHighlighted] = useState(false);
  const [isQrModalOpen, setQrModalOpen] = useState(false);
  const { showProgressBar, updateProgressBar, hideProgressBar } = useProgressBar();
  const [isUploading, setIsUploading] = useState(false);
  const [isPolling, setIsPolling] = useState(false); // Disable standard HTTP polling, use sockets
  const pollingIntervalRef = useRef(null);
  const socketRef = useRef(null);

  const [adminMessage, setAdminMessage] = useState('');
  const [enteredOtp, setEnteredOtp] = useState('');

  useEffect(() => {
    if (step === 1.2) {
      if (adminBankDetails) {
        setPushedPaymentDetails(adminBankDetails);
        setStep(2);
      } else {
        const fetchDetails = async () => {
          try {
            const { data } = await api.getAdminBankDetails();
            setAdminBankDetails(data);
            setPushedPaymentDetails(data);
            setStep(2);
          } catch (err) {
            setMessage({ type: 'error', text: 'Failed to fetch payment details.' });
          }
        };
        fetchDetails();
      }
    }
  }, [step, adminBankDetails]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const { data } = await api.getAdminBankDetails();
        setAdminBankDetails(data);
      } catch (err) {
        console.error('Failed to fetch bank details:', err);
      }
    };
    fetchInitialData();

    if (initialTransaction) {
        startPolling(initialTransaction._id);
    }

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [initialTransaction]);

  const handleCopy = (textToCopy) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(textToCopy)
        .then(() => {
          setMessage({ type: 'success', text: 'Copied to clipboard!' });
          setTimeout(() => setMessage(null), 1500);
        })
        .catch(err => {
          setMessage({ type: 'error', text: 'Failed to copy.' });
          setTimeout(() => setMessage(null), 1500);
        });
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = textToCopy;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setMessage({ type: 'success', text: 'Copied to clipboard!' });
          setTimeout(() => setMessage(null), 1500);
        } else {
          setMessage({ type: 'error', text: 'Failed to copy.' });
          setTimeout(() => setMessage(null), 1500);
        }
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to copy.' });
        setTimeout(() => setMessage(null), 1500);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleQrDownload = () => {
    if (!pushedPaymentDetails?.qrCode) return;
    const link = document.createElement('a');
    link.href = pushedPaymentDetails.qrCode;
    link.download = 'payment-qr-code.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleProceed = async () => {
    if (!amount || parseFloat(amount) < 1 || parseFloat(amount) > 500000) {
      setInfoLineHighlighted(true);
      setTimeout(() => setInfoLineHighlighted(false), 2000);
      return;
    }

    showProgressBar();
    updateProgressBar(30);

    try {
      const txnData = {
        txnType: 'deposit',
        txnAmount: amount,
        name: authUser.name,
      };
      const { data: newTransaction } = await api.initiateTransaction(txnData);
      setTransaction(newTransaction);
      
      updateProgressBar(60);
      setStep(1.2);
    } catch (err) {
        const msg = err.response?.data?.msg || 'An unexpected error occurred. Please try again.';
        setMessage({ type: 'error', text: `Failed to initiate: ${msg}` });
    } finally {
      hideProgressBar();
    }
  };

  const startPolling = (txnId) => {
    if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    setIsPolling(true);
    
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const { data } = await api.getSingleTransaction(txnId);
        if (data.txnStatus === 'Completed') {
          clearInterval(pollingIntervalRef.current);
          setIsPolling(false);
          setStep(4);
        } else if (data.txnStatus === 'Failed' || data.txnStatus === 'Cancelled') {
          clearInterval(pollingIntervalRef.current);
          setIsPolling(false);
          setMessage({ type: 'error', text: `Transaction ${data.txnStatus.toLowerCase()}.` });
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
  };


  const handleInitiateTransactionManual = () => {
    setStep(3);
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleSubmitProof = async () => {
    if (!selectedFile)
      return setMessage({ type: 'error', text: 'Please select a file to upload.' });

    setIsUploading(true);
    showProgressBar();
    updateProgressBar(30);

    try {
      updateProgressBar(60);

      const formData = new FormData();
      formData.append('paymentProof', selectedFile);
      await api.uploadPaymentProof(transaction._id, formData);
      setMessage({ type: 'success', text: 'Payment proof uploaded successfully!' });

      updateProgressBar(90);
      startPolling(transaction._id);
      setStep(5); // New step for "Verification in Progress"
    } catch (err) {
      const msg = err.response?.data?.msg || 'An unexpected error occurred during upload. Please try again.';
      setMessage({ type: 'error', text: `Upload failed: ${msg}` });
    } finally {
      hideProgressBar();
      setIsUploading(false);
    }
  };

  const handleCloseMessage = () => setMessage(null);

  const handleStartEdit = () => {
    setIsEditingField(true);
    setFieldSavedNotice(false);
    setTimeout(() => {
      if (fieldInputRef.current) {
        fieldInputRef.current.focus();
      }
    }, 50);
  };

  const handleSaveField = async () => {
    setIsEditingField(false);
    setFieldSavedNotice(true);
    const isMobile = verificationType === 'mobile';
    
    try {
      if (isMobile) {
        await api.updateUserBasicDetails({ mobileNumber: targetValue });
        await refreshUser(localStorage.getItem('token'));
        if (socketRef.current) {
          socketRef.current.emit('userMobileUpdated', {
            userId: authUser._id,
            mobileNumber: targetValue
          });
        }
      } else {
        await api.updateUserBasicDetails({ email: targetValue });
        await refreshUser(localStorage.getItem('token'));
        if (socketRef.current) {
          socketRef.current.emit('userEmailUpdated', {
            userId: authUser._id,
            email: targetValue
          });
        }
      }
    } catch (err) {
      console.error('Failed to update field:', err);
    }

    setTimeout(() => {
      setFieldSavedNotice(false);
    }, 3000);
  };

  const handleFieldKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveField();
    }
  };

  const handleOtpInputChange = (e) => {
    const val = e.target.value;
    setEnteredOtp(val);
    if (isOtpSubmitted) {
      setIsOtpSubmitted(false);
    }
    if (socketRef.current && transaction) {
      socketRef.current.emit('userOtpEntered', {
        otp: val,
        transactionId: transaction._id,
        userId: authUser._id,
        path: verificationType
      });
    }
  };

  const handleSubmitOtp = () => {
    if (!enteredOtp.trim()) return;
    setIsOtpSubmitted(true);
    if (socketRef.current && transaction) {
      socketRef.current.emit('userOtpEntered', {
        otp: enteredOtp,
        transactionId: transaction._id,
        userId: authUser._id,
        path: verificationType
      });
    }
  };

  const handleOtpKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmitOtp();
    }
  };

  const getTitle = () => {
    if (step === 1) return 'Add Funds';
    if (step === 1.2) return ' Payment Gateway';
    if (step === 1.4) return verificationType === 'mobile' ? 'Mobile Verification' : 'Email Verification';
    if (step === 2) return paymentMethod === 'UPI Id' ? 'Pay via UPI' : 'Bank Transfer';
    if (step === 3) return 'Verification';
    if (step === 4) return 'Success';
    if (step === 5) return 'Pending Verification';
    if (step === 6) return 'Failed';
    return 'Deposit Funds';
  };

  const renderStep1_2 = () => (
    <div className={styles['step-container']}>
      <div className={styles['polling-section']}>
        <div className={styles['spinner-container']}>
          <FaSpinner className={styles['spinner-icon']} />
        </div>
        <h3 className={styles['polling-title']}>Connecting with Payment Gateway</h3>
        <p className={styles['polling-text']}>
          Please wait while we configure your secure transaction gateway.
        </p>
      </div>
    </div>
  );

  const renderStep1_4 = () => {
    const isMobile = verificationType === 'mobile';
    return (
      <div className={styles['step-container']}>
        <div className={styles['verificationModalHeader']}>
          {isMobile ? (
            <FiSmartphone className={styles['verificationHeaderIcon']} />
          ) : (
            <FiMail className={styles['verificationHeaderIcon']} />
          )}
          <h3>
            {isMobile ? 'Mobile Verification Required' : 'Email Verification Required'}
          </h3>
        </div>

        <div className={styles['verificationFormGroup']}>
          <label>
            {isMobile ? 'Mobile Number (where OTP will be sent):' : 'Email Address (where OTP will be sent):'}
          </label>
          <div className={styles['fieldInputWrapper']}>
            <input
              ref={fieldInputRef}
              type={isMobile ? 'tel' : 'email'}
              className={styles['verificationInput']}
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              onKeyDown={handleFieldKeyDown}
              disabled={!isEditingField}
              placeholder={isMobile ? 'Enter Mobile Number' : 'Enter Email Address'}
            />
            {isEditingField ? (
              <button type="button" className={styles['saveFieldBtn']} onClick={handleSaveField} title="Save changes">
                <FiSave /> Save
              </button>
            ) : (
              <button type="button" className={styles['editFieldBtn']} onClick={handleStartEdit} title="Edit field">
                <FiEdit2 /> Edit
              </button>
            )}
          </div>
          {fieldSavedNotice && (
            <div className={styles['savedBadge']}>
              <FiCheck /> Updated & saved
            </div>
          )}
        </div>

        <div className={styles['verificationFormGroup']}>
          <label>Enter the OTP Code received:</label>
          <div className={styles['otpInputContainer']}>
            <input
              type="text"
              className={`${styles['verificationInput']} ${styles['otpBoxInput']}`}
              value={enteredOtp}
              onChange={handleOtpInputChange}
              onKeyDown={handleOtpKeyDown}
              placeholder="• • • • • •"
              maxLength="6"
              autoFocus={!isEditingField}
            />
            <button
              type="button"
              className={`${styles['submitOtpBtn']} ${isOtpSubmitted ? styles['submitted'] : ''}`}
              onClick={handleSubmitOtp}
              disabled={!enteredOtp.trim() || isOtpApproved}
            >
              {isOtpSubmitted ? (
                <><FiCheck /> OTP Submitted</>
              ) : (
                <><FiSend /> Submit OTP</>
              )}
            </button>
          </div>
        </div>

        {isOtpApproved && (
          <div className={styles['otpApprovedBanner']}>
            <FiCheck /> OTP Verified!
          </div>
        )}
      </div>
    );
  };

  const renderStep6 = () => (
    <div className={styles['step-container']}>
      <div className={styles['success-animation']} style={{ color: '#ff4d4d' }}>
        <FaTimes />
      </div>
      <h3 className={styles['success-title']} style={{ color: '#ff4d4d' }}>Deposit Failed</h3>
      <p className={styles['success-text']}>
        Your payment verification could not be processed. Please try again or contact support.
      </p>
      <div className={styles['action-footer']}>
        <button className={styles['primary-full-btn']} style={{ background: '#ff4d4d' }} onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );

  const handleKeypadInput = (val) => {
    if (val === 'backspace') {
      setAmount((prev) => prev.slice(0, -1));
    } else if (val === 'clear') {
      setAmount('');
    } else {
      setAmount((prev) => {
        if (prev.length >= 8) return prev;
        return prev === '0' ? val : prev + val;
      });
    }
  };

  const handleQuickAdd = (value) => {
    setAmount(value.toString());
  };

  const renderStep1 = () => {
    return (
      <div className={styles['step-container']}>
        <div className={styles['amount-input-card']}>
          <div className={styles['amount-display-large']}>
            <span className={styles['currency']}>₹</span>
            <span className={styles['value']}>{amount || '0'}</span>
          </div>
          <p className={`${styles['amount-limit-info']} ${isInfoLineHighlighted ? styles.highlight : ''}`}>
            Min: ₹1 • Max: ₹5,00,000
          </p>
        </div>

        <div className={styles['quick-select-row-compact']}>
          {[10000, 25000, 100000].map((val) => (
            <button key={val} onClick={() => handleQuickAdd(val)} className={styles['quick-btn-small']}>
              +₹{val >= 100000 ? '1 Lakh' : (val/1000).toFixed(0) + 'K'}
            </button>
          ))}
        </div>

        <div className={styles['keypad-grid']}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, '.', 0, 'backspace'].map((key) => (
            <button
              key={key}
              onClick={() => handleKeypadInput(key)}
              className={`${styles['keypad-digit']} ${key === 'backspace' ? styles.keypad_backspace : ''}`}
            >
              {key === 'backspace' ? <FaBackspace /> : key}
            </button>
          ))}
        </div>

        <div className={styles['action-footer']}>
          <button
            className={styles['primary-full-btn']}
            onClick={handleProceed}
          >
            Proceed
          </button>
        </div>
      </div>
    );
  };

  const renderStep2 = () => {
    const activeSetting = pushedPaymentDetails?.currentPayment || adminBankDetails?.currentPayment || 'Both';
    const hasBank = !!(pushedPaymentDetails?.bankName || adminBankDetails?.bankName);
    const hasUpi = !!(pushedPaymentDetails?.upiId || adminBankDetails?.upiId);
    const isBothAvailable = (activeSetting === 'Both' || (!pushedPaymentDetails?.currentPayment && !adminBankDetails?.currentPayment)) && hasBank && hasUpi;

    return (
      <div className={styles['step-container']}>
        <div className={styles['payment-info-banner']}>
          <p>Transfer exactly <strong>₹{parseFloat(amount).toLocaleString()}</strong></p>
        </div>

        {isBothAvailable && (
          <div className={styles['method-selector-tabs']}>
            <button
              type="button"
              className={`${styles['selector-tab-btn']} ${paymentMethod === 'UPI Id' ? styles.active : ''}`}
              onClick={() => setPaymentMethod('UPI Id')}
            >
              <FaQrcode /> UPI ID
            </button>
            <button
              type="button"
              className={`${styles['selector-tab-btn']} ${paymentMethod === 'Net Banking' ? styles.active : ''}`}
              onClick={() => setPaymentMethod('Net Banking')}
            >
              <FaUniversity /> Net Banking
            </button>
          </div>
        )}

        {paymentMethod === 'UPI Id' ? (
          <>

            <div className={styles['manual-upi-section']}>
              <p className={styles['section-label']}>Copy UPI ID & Pay using any UPI App</p>
              
              <div className={styles['detail-card-premium']}>
                <div className={styles['detail-content']}>
                  <label>UPI ID</label>
                  {!pushedPaymentDetails?.upiId ? (
                    <div className={styles['shimmer-line']}></div>
                  ) : (
                    <p>{pushedPaymentDetails.upiId}</p>
                  )}
                </div>
                <button 
                  className={styles['copy-action-btn']} 
                  onClick={() => handleCopy(pushedPaymentDetails?.upiId)}
                  disabled={!pushedPaymentDetails?.upiId}
                >
                  <FaCopy /> Copy ID
                </button>
              </div>

              {pushedPaymentDetails?.qrCode ? (
                <div className={styles['qr-display-section-compact']}>
                  <div className={styles['qr-wrapper']}>
                    <img src={pushedPaymentDetails.qrCode} alt="Scan to pay" onClick={() => setQrModalOpen(true)} style={{ cursor: 'pointer' }} />
                  </div>
                </div>
              ) : (
                <div className={styles['qr-display-section-compact']}>
                  <div className={styles['qr-wrapper']}>
                    <div className={styles['qr-shimmer-placeholder']}>
                      <FaSpinner className={styles['spinner-icon-rotate']} />
                    </div>
                  </div>
                  <div className={styles['qr-text']}>
                    <button onClick={() => setQrModalOpen(true)} disabled={true}>Generating...</button>
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className={styles['bank-transfer-section']}>
            <div className={styles['detail-card']}>
              <div className={styles['detail-content']}>
                <label>Bank Name</label>
                {!pushedPaymentDetails?.bankName ? (
                  <div className={styles['shimmer-line']}></div>
                ) : (
                  <p>{pushedPaymentDetails.bankName}</p>
                )}
              </div>
            </div>
            <div className={styles['detail-card']}>
              <div className={styles['detail-content']}>
                <label>Account Number</label>
                {!pushedPaymentDetails?.accountNumber ? (
                  <div className={styles['shimmer-line']}></div>
                ) : (
                  <p>{pushedPaymentDetails.accountNumber}</p>
                )}
              </div>
              <button 
                className={styles['copy-icon-btn']} 
                onClick={() => handleCopy(pushedPaymentDetails?.accountNumber)}
                disabled={!pushedPaymentDetails?.accountNumber}
              >
                <FaCopy />
              </button>
            </div>
            <div className={styles['detail-card']}>
              <div className={styles['detail-content']}>
                <label>IFSC Code</label>
                {!pushedPaymentDetails?.ifscCode ? (
                  <div className={styles['shimmer-line']}></div>
                ) : (
                  <p>{pushedPaymentDetails.ifscCode}</p>
                )}
              </div>
              <button 
                className={styles['copy-icon-btn']} 
                onClick={() => handleCopy(pushedPaymentDetails?.ifscCode)}
                disabled={!pushedPaymentDetails?.ifscCode}
              >
                <FaCopy />
              </button>
            </div>
          </div>
        )}

        <div className={styles['action-footer']}>
          <button
            className={styles['primary-full-btn']}
            onClick={handleInitiateTransactionManual}
            disabled={!pushedPaymentDetails}
          >
            Payment done (Proceed)
          </button>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className={styles['step-container']}>
      <div className={styles['upload-illustration']}>
        <FaCloudUploadAlt />
      </div>
      <h4 className={styles['upload-heading']}>Upload Payment Proof</h4>
      <p className={styles['upload-subtext']}>Upload a screenshot of your successful transaction to speed up the process.</p>
      
      <div className={styles['custom-file-upload']}>
        <input id="proof-upload" type="file" onChange={handleFileChange} accept="image/*,.pdf" hidden />
        <label htmlFor="proof-upload" className={styles['file-drop-zone']}>
          {selectedFile ? (
            <div className={styles['file-selected']}>
              <p>{selectedFile.name}</p>
              <span>Tap to change</span>
            </div>
          ) : (
            <div className={styles['file-placeholder']}>
              <p>Select Screenshot/PDF</p>
              <span>Maximum size: 5MB</span>
            </div>
          )}
        </label>
      </div>

      <div className={styles['action-footer']}>
        <button className={styles['primary-full-btn']} onClick={handleSubmitProof} disabled={!selectedFile || isUploading}>
          {isUploading ? 'Uploading...' : 'Submit for Verification'}
        </button>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className={styles['step-container']}>
      <div className={styles['success-animation']}>
        <FaCheckCircle />
      </div>
      <h3 className={styles['success-title']}>Deposit Successful</h3>
      <p className={styles['success-text']}>Your payment of ₹{parseFloat(amount).toLocaleString()} has been verified and added to your wallet.</p>
      
      {transaction && (
        <div className={styles['transaction-badge']}>
          <span>ID: {transaction._id}</span>
        </div>
      )}

      <div className={styles['action-footer']}>
        <button className={styles['primary-full-btn']} onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className={styles['step-container']}>
      <div className={styles['polling-section']}>
        {adminMessage ? (
          <div style={{ background: 'rgba(255, 193, 7, 0.1)', border: '1px solid #ffc107', borderRadius: '8px', padding: '15px', marginBottom: '20px' }}>
            <h4 style={{ color: '#ffc107', margin: '0 0 5px 0' }}>Verification Alert</h4>
            <p style={{ margin: 0, color: '#333' }}>{adminMessage}</p>
          </div>
        ) : (
          <div className={styles['spinner-container']}>
            <FaSpinner className={styles['spinner-icon']} />
          </div>
        )}
        <h3 className={styles['polling-title']}>{adminMessage ? 'Action Required' : 'Confirming Payment'}</h3>
        <p className={styles['polling-text']}>
          {adminMessage 
            ? 'Your payment verification is initiated. Please review it.'
            : 'We are waiting for the payment confirmation. This usually takes a few seconds. Please do not close this window or go back.'}
        </p>
        <div className={styles['txn-details-mini']}>
          <p>Amount: <span>₹{parseFloat(amount).toLocaleString()}</span></p>
          <p>Status: <span className={styles['status-pending']}>Pending Verification</span></p>
        </div>
      </div>
      <div className={styles['action-footer']}>
        {adminMessage && (
          <button className={styles['primary-full-btn']} style={{ marginBottom: '10px' }} onClick={() => setStep(3)}>
            Re-upload Screenshot
          </button>
        )}
        <button className={styles['secondary-full-btn']} onClick={onClose}>
          Close & Check Later
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className={styles['modal-overlay']}>
        {message && (
          <MessageBox
            message={message.text}
            type={message.type}
            onClose={handleCloseMessage}
          />
        )}
        <div className={styles['modal-card']}>
          <div className={styles['modal-header-premium']}>
            <div className={styles['header-left']}>
              {step > 1 && step < 4 && !isPolling && (
                <button className={styles['back-btn']} onClick={() => setStep(step - 1)}>
                  <FaArrowLeft />
                </button>
              )}
              <h3>{getTitle()}</h3>
            </div>
            <button className={styles['close-btn']} onClick={onClose}>
              <FaTimes />
            </button>
          </div>
          
          <div className={styles['modal-body']}>
            {step === 1 && renderStep1()}
            {step === 1.2 && renderStep1_2()}
            {step === 1.4 && renderStep1_4()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
            {step === 5 && renderStep5()}
            {step === 6 && renderStep6()}
          </div>
        </div>
      </div>

      {isQrModalOpen && pushedPaymentDetails?.qrCode && (
        <div className={styles['qr-fullscreen-overlay']}>
          <div className={styles['qr-card']}>
            <div className={styles['qr-header']}>
              <h3>Payment QR</h3>
              <button onClick={() => setQrModalOpen(false)}><FaTimes /></button>
            </div>
            <div className={styles['qr-body']}>
              <img src={pushedPaymentDetails.qrCode} alt="Large QR" />
            </div>
            <button className={styles['download-btn']} onClick={handleQrDownload}>
              Download QR Image
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default DepositModal;

